import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, CircularProgress, Divider, IconButton,
  TextField, Typography,
} from '@mui/material';
import {
  AcUnit, Add, DeleteOutline, DeviceThermostat, DirectionsRun, DoorFront, Opacity, PersonPin, ShowChart, Speed, WaterDrop,
} from '@mui/icons-material';
import { SensorHistoryDialog } from './SensorHistoryDialog';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { MAX_WIDTH } from '../App';
import { useAutoRefresh } from '../hooks/useAutoRefresh';

interface SensorSource {
  id: string;
  name: string;
  url: string;
}

interface SensorReading {
  id: string;
  name: string;
  type: string;
  value: number;
  unit: string;
  updatedAt: number;
}

interface SourceData {
  readings: SensorReading[];
  error: string | null;
  loading: boolean;
}

const SENSOR_ICONS: Record<string, React.ComponentType<{ sx?: object }>> = {
  temperature: DeviceThermostat,
  humidity:    Opacity,
  pressure:    Speed,
  co2:         AcUnit,
  moisture:    WaterDrop,
  motion:      DirectionsRun,
  presence:    PersonPin,
  contact:     DoorFront,
  leak:        WaterDrop,
};

const LOGGABLE_TYPES = new Set(['temperature', 'humidity', 'co2', 'pressure', 'moisture']);

const defaultIcon = DeviceThermostat;

const groupReadings = (readings: SensorReading[]): SensorReading[][] => {
  const groups = new Map<string, SensorReading[]>();
  for (const r of readings) {
    const key = r.id.substring(0, r.id.lastIndexOf('_'));
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(r);
  }
  return Array.from(groups.values());
};

const formatAge = (ts: number): string => {
  const sec = Math.floor((Date.now() - ts) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
};

interface Props {
  sources: SensorSource[];
  onSourcesChange: (sources: SensorSource[]) => void;
}

export const SensorsPage: React.FC<Props> = ({ sources, onSourcesChange }) => {
  const { t } = useLanguage();
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState<Record<string, SourceData>>({});
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState<{ id: string; name: string; unit: string; sourceUrl: string } | null>(null);
  const [internalRefreshKey, setInternalRefreshKey] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchSource = useCallback(async (source: SensorSource) => {
    setData(prev => ({ ...prev, [source.id]: { ...prev[source.id], loading: true, error: null, readings: prev[source.id]?.readings ?? [] } }));
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      const url = source.url.replace(/\/$/, '') + '/sensors';
      const resp = await fetch(url, { headers: getAuthHeader(), signal: controller.signal });
      clearTimeout(timer);
      if (!mountedRef.current) return;
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const readings: SensorReading[] = await resp.json();
      setData(prev => ({ ...prev, [source.id]: { readings, loading: false, error: null } }));
    } catch {
      if (!mountedRef.current) return;
      setData(prev => ({ ...prev, [source.id]: { readings: prev[source.id]?.readings ?? [], loading: false, error: t.sensorsErrorFetch } }));
    }
  }, [getAuthHeader, t.sensorsErrorFetch]);

  useEffect(() => {
    sources.forEach(s => fetchSource(s));
  }, [sources, internalRefreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useAutoRefresh(() => setInternalRefreshKey(k => k + 1), 60_000);

  const handleAddSource = () => {
    if (!newName.trim() || !newUrl.trim()) return;
    const id = Date.now().toString();
    onSourcesChange([...sources, { id, name: newName.trim(), url: newUrl.trim() }]);
    setNewName('');
    setNewUrl('');
    setAddOpen(false);
  };

  const handleRemove = (id: string) => {
    onSourcesChange(sources.filter(s => s.id !== id));
    setData(prev => { const next = { ...prev }; delete next[id]; return next; });
  };

  return (
    <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', width: '100%', p: 2 }}>

      {sources.length === 0 && !addOpen && (
        <Box textAlign="center" mt={6} mb={4}>
          <Typography variant="body1" color="text.secondary">{t.sensorsEmpty}</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>{t.sensorsAddSource}</Typography>
        </Box>
      )}

      {sources.map(source => {
        const sd = data[source.id];
        return (
          <Box key={source.id} sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center" mb={1}>
              <Typography variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>{source.name}</Typography>
              {sd?.loading && <CircularProgress size={16} sx={{ mr: 1 }} />}
              <IconButton size="small" aria-label={t.sensorsRemoveSource} onClick={() => handleRemove(source.id)}>
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Box>

            {sd?.error && (
              <Alert severity="warning" sx={{ mb: 1 }}>{sd.error}</Alert>
            )}

            {groupReadings(sd?.readings ?? []).map(group => {
              const first = group[0];
              const baseName = first.name.split(' · ')[0];
              const Icon = SENSOR_ICONS[first.type] ?? defaultIcon;
              return (
                <Box key={group.map(r => r.id).join(',')} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                    <Icon sx={{ fontSize: 24, color: 'primary.main' }} />
                  </Avatar>
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" noWrap>{baseName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.sensorsLastUpdated}: {formatAge(first.updatedAt * 1000)}
                    </Typography>
                  </Box>
                  {group.map(r => (
                    <Box key={r.id} sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                        {r.value}{r.unit}
                      </Typography>
                      {LOGGABLE_TYPES.has(r.type) && (
                        <IconButton
                          size="small"
                          aria-label={`${t.sensorHistory}: ${r.name}`}
                          onClick={() => setHistoryFor({ id: r.id, name: r.name, unit: r.unit, sourceUrl: source.url })}
                        >
                          <ShowChart fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Box>
              );
            })}

            {!sd?.loading && !sd?.error && (sd?.readings ?? []).length === 0 && (
              <Typography variant="body2" color="text.secondary">{t.sensorsEmpty}</Typography>
            )}

            <Divider sx={{ mt: 1 }} />
          </Box>
        );
      })}

      {addOpen && (
        <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <TextField
            fullWidth autoFocus label={t.sensorsSourceName} value={newName}
            onChange={e => setNewName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth label={t.sensorsSourceUrl} value={newUrl}
            onChange={e => setNewUrl(e.target.value)}
            placeholder="https://device.example.com/switchbot"
            onKeyDown={e => e.key === 'Enter' && handleAddSource()}
            sx={{ mb: 2 }}
          />
          <Box display="flex" gap={1} justifyContent="flex-end">
            <Button onClick={() => setAddOpen(false)}>{t.nodeEditCancel}</Button>
            <Button variant="contained" onClick={handleAddSource} disabled={!newName.trim() || !newUrl.trim()}>
              {t.sensorsAddButton}
            </Button>
          </Box>
        </Box>
      )}

      {!addOpen && (
        <Button startIcon={<Add />} onClick={() => setAddOpen(true)} fullWidth variant="outlined" sx={{ mt: 1 }}>
          {t.sensorsAddButton}
        </Button>
      )}

      <SensorHistoryDialog
        open={historyFor !== null}
        sensorId={historyFor?.id ?? null}
        sensorName={historyFor?.name ?? ''}
        sensorUnit={historyFor?.unit ?? ''}
        sourceUrl={historyFor?.sourceUrl ?? ''}
        getAuthHeader={getAuthHeader}
        onClose={() => setHistoryFor(null)}
      />

    </Box>
  );
};

export type { SensorSource };
